import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBulkSelection } from '../contexts/BulkSelectionContext';
import { Product, productService } from '../services/product';
import { ProductCard } from '../components/products/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardTitle, CardHeader } from '../components/ui/card';
import { WarningBanner } from '../components/ui/warning-banner';
import { ArrowLeft, Package2, Search, Loader2, ChevronLeft, ChevronRight, Trash2, RefreshCw, Eye, Filter, Activity } from 'lucide-react';

export const BulkReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    selection, 
    isSelectionActive, 
    removeFromSelection, 
    clearSelection,
    setCachedProducts,
    getCachedProducts,
    isCacheValid
  } = useBulkSelection();
  const [products, setProducts] = useState<Product[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  const selectedIds = Array.from(selection.selectedIds);

  useEffect(() => {
    if (!isSelectionActive) {
      navigate('/products');
      return;
    }
    loadProducts();
  }, [isSelectionActive, currentPage, selection.selectedIds.size, searchQuery]);

  const loadProducts = async () => {
    try {
      const hasCachedData = isCacheValid(searchQuery);
      // Only show full loading if we don't have any cached data
      setLoading(!hasCachedData);
      // Show page loading if we have cached data but need to fetch new page
      setPageLoading(hasCachedData);
      setError(null);

      if (selectedIds.length === 0) {
        setProducts([]);
        setLoading(false);
        setPageLoading(false);
        return;
      }

      // Check if we have valid cached data first
      const cachedData = getCachedProducts(searchQuery);
      if (cachedData && cachedData.pagination.page === currentPage) {
        console.log(`Using cached data for page ${currentPage} - avoiding API call`);
        
        // Ensure cached products are also sorted
        const sortedCachedProducts = cachedData.products.sort((a, b) => 
          a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
        );
        
        setProducts(sortedCachedProducts);
        setPagination(cachedData.pagination);
        setLoading(false);
        setPageLoading(false);
        
        return; // Skip API call
      }

      // Check if this is just a page change and we can use existing pagination info
      if (cachedData && cachedData.searchQuery === searchQuery) {
        console.log(`Cache exists for search "${searchQuery}" but different page - fetching page ${currentPage}`);
        // We have cache for this search but different page, we still need to fetch
        // but we can use cached pagination total for immediate feedback
        setPagination(prev => ({
          ...prev,
          total: cachedData.pagination.total,
          pages: cachedData.pagination.pages
        }));
      } else {
        console.log('No valid cache found - fetching from API');
      }
      
      // Fetch from API
      const response = await productService.getBulkProducts(
        selectedIds, 
        currentPage, 
        pagination.limit, 
        searchQuery || undefined
      );
      
      // Sort products alphabetically by name for consistent ordering
      const sortedProducts = response.products.sort((a, b) => 
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      );
      
      setProducts(sortedProducts);
      setPagination(response.pagination);

      // Cache the sorted data for future use
      setCachedProducts(sortedProducts, response.pagination, searchQuery);

    } catch (error: any) {
      setError(error.message || 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const handleRemoveProduct = (productId: string) => {
    // Remove from global selection state
    removeFromSelection(productId);
    
    // Remove from local state immediately for better UX - keeps order
    setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
    
    // Update pagination totals to reflect the removal
    setPagination(prev => ({
      ...prev,
      total: prev.total - 1,
      pages: Math.ceil((prev.total - 1) / prev.limit)
    }));
    
    // If this was the last product on the current page, go to previous page
    if (products.length === 1 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
    
    // Clear cache for current search to ensure fresh data on next load
    // but don't reload immediately to preserve the current view
    const cachedData = getCachedProducts(searchQuery);
    if (cachedData) {
      // Update cached data to remove the deleted product
      const updatedProducts = cachedData.products.filter(p => p.id !== productId);
      const updatedPagination = {
        ...cachedData.pagination,
        total: cachedData.pagination.total - 1,
        pages: Math.ceil((cachedData.pagination.total - 1) / cachedData.pagination.limit)
      };
      setCachedProducts(updatedProducts, updatedPagination, searchQuery);
    }
  };

  const scrollToTop = () => {
    // Try multiple methods to ensure scrolling works
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Also try document.body in case of layout issues
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    setTimeout(() => scrollToTop(), 100);
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInput);
    // Reset to first page when searching
    setCurrentPage(1);
    // Scroll to top when searching
    setTimeout(() => scrollToTop(), 100);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
    // Scroll to top when clearing search
    setTimeout(() => scrollToTop(), 100);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleBackToProducts = () => {
    navigate('/products');
  };

  const handleBulkProcess = () => {
    // Navigate to jobs page to create processing jobs
    navigate('/jobs');
  };

  if (!isSelectionActive) {
    return null; // Will redirect via useEffect
  }

  if (loading && products.length === 0) {
    return (
      <div className="space-y-8">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Button variant="outline" onClick={handleBackToProducts} className="gap-2 mb-3">
              <ArrowLeft className="h-4 w-4" />
              Back to Products
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Bulk Review</h1>
            <p className="text-muted-foreground">
              Loading your selected products...
            </p>
          </div>
        </div>

        {/* Smart Loading Card */}
        <Card className="card-enhanced bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <CardContent className="flex flex-col items-center justify-center py-20 space-y-8">
            {/* Animated Loader */}
            <div className="relative">
              <div className="w-20 h-20 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package2 className="h-8 w-8 text-primary animate-pulse" />
              </div>
            </div>
            
            {/* Main Message */}
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-bold text-foreground">
                Fetching {selection.totalCount.toLocaleString()} Selected Products
              </h3>
              <p className="text-muted-foreground max-w-lg text-lg">
                We're retrieving your selected products from the database. Large selections may take a moment to process.
              </p>
            </div>

            {/* Progress Info Card */}
            <Card className="card-enhanced min-w-80">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Selected Products:</span>
                  <span className="text-lg font-bold text-primary">{selection.totalCount.toLocaleString()}</span>
                </div>
                
                {selection.category && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Category:</span>
                    <span className="font-semibold text-foreground bg-primary/10 px-3 py-1 rounded-full text-sm">
                      {selection.category}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Status:</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    <span className="font-medium text-primary">Processing...</span>
                  </div>
                </div>

                {/* Estimated time based on selection size */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Estimated time:</span>
                  <span className="text-sm font-medium text-foreground">
                    {selection.totalCount < 100 ? '< 5 seconds' :
                     selection.totalCount < 1000 ? '5-15 seconds' :
                     selection.totalCount < 5000 ? '15-45 seconds' :
                     '1-2 minutes'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Performance Note */}
            <div className="text-center max-w-md">
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-primary mb-1">Smart Caching Enabled</p>
                    <p className="text-xs text-muted-foreground">
                      Large selections are processed in optimized batches. Once loaded, navigation will be instant thanks to intelligent caching.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading Animation Bar */}
            <div className="w-full max-w-md">
              <div className="bg-muted/50 rounded-full h-2 overflow-hidden">
                <div className="bg-gradient-to-r from-primary to-accent h-full rounded-full animate-pulse"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="flex items-center justify-between animate-slide-down">
        <div className="space-y-1">
          <Button variant="outline" onClick={handleBackToProducts} className="gap-2 mb-3">
            <ArrowLeft className="h-4 w-4" />
            Back to Products
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">Bulk Review</h1>
          <p className="text-muted-foreground">
            Reviewing <span className="font-medium text-foreground">{selection.totalCount}</span> selected products
            {selection.category && (
              <span> from category: <span className="font-semibold text-primary">{selection.category}</span></span>
            )}
          </p>
        </div>
        
        <Button 
          onClick={handleBulkProcess}
          disabled={selection.selectedIds.size === 0}
          className="gap-2 shadow-lg"
        >
          <Activity className="h-4 w-4" />
          Create Processing Jobs
        </Button>
      </div>

      {/* Selection Controls */}
      <WarningBanner
        variant="info"
        title={`${selection.selectedIds.size} of ${selection.totalCount} products selected`}
        message="Individual products can be removed below. Use the search to find specific products within your selection."
        action={{
          label: "Clear All",
          onClick: clearSelection,
          variant: "outline",
        }}
        icon={<Package2 className="h-5 w-5 text-blue-600" />}
      />

      {/* Search within selected */}
      <Card className="card-modern">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Search Selection</span>
              </div>
              
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search within selected products..."
                    value={searchInput}
                    onChange={(e) => handleSearchInputChange(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="pl-10 h-10"
                  />
                </div>
                <Button 
                  onClick={handleSearchSubmit}
                  disabled={loading || pageLoading}
                  size="sm"
                  className="shadow-md gap-2"
                >
                  <Search className="h-4 w-4" />
                  Search
                </Button>
                {searchQuery && (
                  <Button 
                    onClick={handleClearSearch}
                    disabled={loading || pageLoading}
                    variant="outline"
                    size="sm"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            
            <div className="text-sm font-medium text-muted-foreground">
              Page <span className="text-foreground">{pagination.page}</span> of <span className="text-foreground">{pagination.pages}</span>
              {searchQuery && (
                <div className="mt-1">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full border border-primary/20">
                    Filtered: "{searchQuery}"
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <WarningBanner
          variant="error"
          title="Error"
          message={error}
          dismissible
          onDismiss={() => setError('')}
        />
      )}

      {/* Products Grid */}
      {products.length === 0 && !loading && !pageLoading && selectedIds.length === 0 ? (
        <Card className="card-modern">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-muted/50 rounded-full mb-6">
              <Package2 className="h-12 w-12 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl mb-3">No Products Selected</CardTitle>
            <CardDescription className="text-center mb-8 max-w-md">
              No products are currently selected for bulk processing. 
              Go back to the products page to make selections.
            </CardDescription>
            <Button onClick={handleBackToProducts} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Products
            </Button>
          </CardContent>
        </Card>
      ) : products.length === 0 && (loading || pageLoading) ? (
        // Show a minimal loading state when we have selections but no products loaded yet
        <Card className="card-modern">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="relative mb-6">
              <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <Package2 className="absolute inset-0 m-auto h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl mb-3">Loading Selected Products</CardTitle>
            <CardDescription className="text-center max-w-md">
              {pageLoading ? 'Loading from cache...' : `Fetching ${selectedIds.length.toLocaleString()} selected products...`}
            </CardDescription>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Results summary */}
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-muted-foreground">
              Showing <span className="text-foreground">{((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="text-foreground">{pagination.total}</span> selected products
              {isCacheValid(searchQuery) && (
                <span className="ml-2 text-xs bg-success/10 text-success px-2 py-1 rounded-full border border-success/20">
                  Cached
                </span>
              )}
            </div>
            {(loading || pageLoading) && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {pageLoading ? 'Loading page...' : 'Updating...'}
              </div>
            )}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.id} className="relative group">
                <ProductCard product={product} />
                {/* Remove button overlay - moves with card on hover */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none group-hover:-translate-y-1 transition-transform duration-300">
                  <button
                    onClick={() => handleRemoveProduct(product.id)}
                    className="absolute top-3 right-3 z-20 pointer-events-auto bg-destructive/20 border-2 border-destructive/40 text-destructive-foreground hover:bg-destructive/30 hover:border-destructive/60 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200"
                    title="Remove from selection"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Modern Pagination */}
          {pagination.pages > 1 && (
            <Card className="card-modern">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page <span className="font-medium text-foreground">{pagination.page}</span> of <span className="font-medium text-foreground">{pagination.pages}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1 || loading || pageLoading}
                      className="gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    {/* Page numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                        let pageNum: number;
                        if (pagination.pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.pages - 2) {
                          pageNum = pagination.pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === pagination.page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading || pageLoading}
                            className="w-10 h-10"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.pages || loading || pageLoading}
                      className="gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};